/** Heuristic: true when a captured frame is mostly black/empty (common with HW-accelerated NVR apps). */
export function isFrameMostlyBlack(
  imageData: ImageData,
  threshold = 18,
  blackRatio = 0.92
): boolean {
  const { data, width, height } = imageData;
  if (width === 0 || height === 0) return true;

  const step = Math.max(4, Math.floor((width * height) / 12000));
  let dark = 0;
  let samples = 0;

  for (let i = 0; i < data.length; i += 4 * step) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (lum < threshold) dark += 1;
    samples += 1;
  }

  return samples > 0 && dark / samples >= blackRatio;
}

export async function analyzeStreamForBlankVideo(
  stream: MediaStream,
  timeoutMs = 4000
): Promise<{ mostlyBlack: boolean; displaySurface?: string }> {
  const track = stream.getVideoTracks()[0];
  const displaySurface = (track?.getSettings?.() as { displaySurface?: string })?.displaySurface;

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;

    let settled = false;
    const finish = (mostlyBlack: boolean) => {
      if (settled) return;
      settled = true;
      video.pause();
      video.srcObject = null;
      resolve({ mostlyBlack, displaySurface });
    };

    const timer = window.setTimeout(() => finish(false), timeoutMs);

    const sample = () => {
      if (!video.videoWidth || !video.videoHeight) {
        window.setTimeout(sample, 200);
        return;
      }

      const canvas = document.createElement('canvas');
      const w = Math.min(320, video.videoWidth);
      const h = Math.round((w * video.videoHeight) / video.videoWidth);
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        window.clearTimeout(timer);
        finish(false);
        return;
      }
      ctx.drawImage(video, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      window.clearTimeout(timer);
      finish(isFrameMostlyBlack(imageData));
    };

    video.onloadeddata = () => {
      video.play().then(sample).catch(() => finish(false));
    };
    video.onerror = () => finish(false);
  });
}

export function captureSurfaceWarning(displaySurface?: string): string | null {
  if (displaySurface === 'window') {
    return (
      'You shared a single window. Camera apps like SmartPSS often record as a black box in window capture. ' +
      'Stop, then share your entire screen, or disable hardware acceleration in SmartPSS (System Config → Video).'
    );
  }
  return null;
}
